/**
 * API client for website backend (blog, page content, auth).
 *
 * Every GET goes through cache.dedupe() (src/services/cache.js): identical
 * in-flight requests share one network call, and completed responses are
 * cached for a TTL appropriate to how often that data actually changes.
 * Public (non-admin) GETs also persist to localStorage for
 * stale-while-revalidate across page loads — admin/authenticated GETs never
 * do, on purpose (see cache.js's persistence rules).
 *
 * Mutations (POST/PUT/PATCH/DELETE) always bypass the cache and invalidate
 * whatever public GET keys they could have changed, so the next read is
 * never stale after a write.
 */
import cache, { TTL } from './cache';
import { withAttribution } from '../utils/attribution';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_WEBSITE_API_URL || 'http://localhost:8001';

function getAuthHeaders() {
  const token = localStorage.getItem('admin_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Token ${token}`;
  return headers;
}

function getAuthHeadersMultipart() {
  const token = localStorage.getItem('admin_token');
  const headers = {};
  if (token) headers['Authorization'] = `Token ${token}`;
  return headers;
}

function throwIfUnauthorized(res) {
  if (res.status === 401) {
    localStorage.removeItem('admin_token');
    throw new Error('Session expired. Please sign in again.');
  }
}

/**
 * ModelViewSets without an explicit pagination_class now inherit the
 * backend's DEFAULT_PAGINATION_CLASS (PAGE_SIZE=20), so their list
 * responses changed shape from a bare array to {count, next, previous,
 * results}. All of these are small, staff-curated CMS collections
 * (categories, partners, about-stats, testimonials, pricing packages,
 * scholarships, countries) that stay well under 20 items in practice, so
 * unwrapping to `.results` here keeps every existing caller working
 * unchanged. Exception: blog posts can realistically grow past 20 — if
 * that happens, Blog.js needs real "load more" pagination UI wired to
 * `next`/`previous`, not just a wider PAGE_SIZE.
 */
function unwrapList(data) {
  return Array.isArray(data) ? data : (data?.results ?? data);
}

/**
 * Lets one caller abandon a shared (deduped) request without cancelling it
 * for everyone else. The network request itself is never aborted: it is
 * shared by every caller of the same URL, so wiring one caller's signal
 * into fetch() made a second, still-interested caller receive the first
 * one's AbortError (SearchResults showed "Found 0" this way). The response
 * still lands in the cache, so nothing is wasted.
 */
function withAbort(promise, signal) {
  if (!signal) return promise;
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException('Aborted', 'AbortError'));
    if (signal.aborted) { abort(); return; }
    signal.addEventListener('abort', abort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

/** Public GET, deduped + cached + persisted for stale-while-revalidate. */
function publicGet(url, { ttl = TTL.LIST, errorMessage = 'Request failed', notFoundMessage, signal, persist = true } = {}) {
  return withAbort(cache.dedupe(
    url,
    async () => {
      const res = await fetch(url);
      if (!res.ok) {
        // A 404 genuinely doesn't exist — retrying won't help, and the UI
        // should say so rather than blaming the network. Every other
        // failure (backend down, timeout, 500) is worth retrying, so it
        // gets a distinct message a "Try Again" button actually fits.
        if (res.status === 404 && notFoundMessage) throw new Error(notFoundMessage);
        throw new Error(errorMessage);
      }
      return res.json();
    },
    { ttl, persist }
  ), signal);
}

/**
 * Admin GET — same dedup-in-flight benefit, but never persisted to
 * localStorage and cached only briefly (LIST ttl at most), since admin
 * data changes more often and must never leak into the public cache.
 */
function adminGet(url, { ttl = TTL.LIST, errorMessage = 'Request failed' } = {}) {
  return cache.dedupe(
    url,
    async () => {
      const res = await fetch(url, { headers: getAuthHeaders() });
      throwIfUnauthorized(res);
      if (!res.ok) throw new Error(errorMessage);
      return res.json();
    },
    { ttl, persist: false }
  );
}

// --- Auth ---
export async function login(username, password) {
  const res = await fetch(`${API_BASE}/api/auth/login/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = res.status === 429
      ? 'Too many attempts. Wait a minute and try again.'
      : (err.non_field_errors?.[0] || err.error || err.message || `Login failed (${res.status})`);
    throw new Error(msg);
  }
  return res.json();
}

/**
 * Confirm the stored admin token is still valid. Resolves true / false;
 * clears a dead token so the caller can bounce to the login screen.
 */
export async function verifyAuth() {
  const token = localStorage.getItem('admin_token');
  if (!token) return false;
  try {
    const res = await fetch(`${API_BASE}/api/auth/verify/`, {
      headers: { Authorization: `Token ${token}` },
    });
    if (res.status === 401 || res.status === 403) {
      localStorage.removeItem('admin_token');
      return false;
    }
    return res.ok;
  } catch (_) {
    // Network error — don't nuke the token over a transient failure.
    return true;
  }
}

// --- Blog posts (public: GET only; admin: full CRUD with token) ---
export async function getBlogPosts(params = {}, { signal } = {}) {
  const q = new URLSearchParams(params).toString();
  const url = `${API_BASE}/api/blog/posts/${q ? '?' + q : ''}`;
  const data = await publicGet(url, { ttl: TTL.LIST, errorMessage: 'Failed to fetch posts', signal });
  return unwrapList(data);
}

export async function getBlogPostBySlug(slug, { signal } = {}) {
  return publicGet(`${API_BASE}/api/blog/posts/${slug}/`, { ttl: TTL.LIST, errorMessage: 'Post not found', signal });
}

/** Admin: fetch a single post by slug (includes drafts). Use this when editing in the admin portal. */
export async function getBlogPostBySlugAdmin(slug) {
  return adminGet(`${API_BASE}/api/blog/posts/${slug}/`, { errorMessage: 'Post not found' });
}

/** Admin: list all posts (including unpublished) when authenticated */
export async function getBlogPostsAdmin() {
  const data = await adminGet(`${API_BASE}/api/blog/posts/`, { errorMessage: 'Failed to fetch posts' });
  return unwrapList(data);
}

export async function createBlogPost(formData) {
  const headers = getAuthHeadersMultipart();
  const res = await fetch(`${API_BASE}/api/blog/posts/`, {
    method: 'POST',
    headers,
    body: formData,
  });
  throwIfUnauthorized(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || 'Create failed');
  }
  cache.invalidate('/api/blog/posts');
  return res.json();
}

export async function updateBlogPost(slug, formData) {
  const headers = getAuthHeadersMultipart();
  const res = await fetch(`${API_BASE}/api/blog/posts/${slug}/`, {
    method: 'PUT',
    headers,
    body: formData,
  });
  throwIfUnauthorized(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || 'Update failed');
  }
  cache.invalidate('/api/blog/posts');
  return res.json();
}

export async function deleteBlogPost(slug) {
  const res = await fetch(`${API_BASE}/api/blog/posts/${slug}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/posts');
}

// --- Blog categories (public: GET list; admin: full CRUD) ---
export async function getBlogCategories({ signal } = {}) {
  const data = await publicGet(`${API_BASE}/api/blog/categories/`, { ttl: TTL.CONTENT, errorMessage: 'Failed to fetch categories', signal });
  return unwrapList(data);
}

export async function createBlogCategory(data) {
  const res = await fetch(`${API_BASE}/api/blog/categories/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || 'Create failed');
  }
  cache.invalidate('/api/blog/categories');
  return res.json();
}

export async function updateBlogCategory(id, data) {
  const res = await fetch(`${API_BASE}/api/blog/categories/${id}/`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || 'Update failed');
  }
  cache.invalidate('/api/blog/categories');
  return res.json();
}

export async function deleteBlogCategory(id) {
  const res = await fetch(`${API_BASE}/api/blog/categories/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/categories');
}

// --- Page content (public: GET; admin: full CRUD with token) ---
export async function getPageContentByPage(pageName, { signal } = {}) {
  const url = `${API_BASE}/api/blog/page-content/by-page/${pageName}/`;
  return publicGet(url, { ttl: TTL.CONTENT, errorMessage: 'Failed to fetch page content', signal });
}

/** Returns { section_name: text_content } for a page (e.g. global, home). */
export async function getPageContentFlat(pageName, { signal } = {}) {
  const url = `${API_BASE}/api/blog/page-content/by-page-flat/${pageName}/`;
  return publicGet(url, { ttl: TTL.CONTENT, errorMessage: 'Failed to fetch page content', signal });
}

/** Upload image; returns { url }. Auth required. */
export async function uploadMedia(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}/api/blog/media-upload/`, {
    method: 'POST',
    headers: getAuthHeadersMultipart(),
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Upload failed');
  }
  return res.json();
}

export async function getPageContentList() {
  const data = await adminGet(`${API_BASE}/api/blog/page-content/`, { errorMessage: 'Failed to fetch page content list' });
  return unwrapList(data);
}

export async function createPageContent(data) {
  const res = await fetch(`${API_BASE}/api/blog/page-content/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || `Create failed (${res.status})`);
  }
  cache.invalidate('/api/blog/page-content');
  return res.json();
}

export async function updatePageContent(id, data) {
  const res = await fetch(`${API_BASE}/api/blog/page-content/${id}/`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || `Update failed (${res.status})`);
  }
  cache.invalidate('/api/blog/page-content');
  return res.json();
}

export async function deletePageContent(id) {
  const res = await fetch(`${API_BASE}/api/blog/page-content/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/page-content');
}

// --- Registrations (admin: list + retry sync) ---
export async function getRegistrations() {
  return adminGet(`${API_BASE}/api/registrations/list/`, { errorMessage: 'Failed to fetch registrations' });
}

export async function retrySyncRegistration(id) {
  const res = await fetch(`${API_BASE}/api/registrations/${id}/retry-sync/`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.message || 'Retry failed');
  }
  cache.invalidate('/api/registrations');
  return res.json();
}

// --- Pricing packages (public: GET; admin: full CRUD with token) ---
export async function getPricingPackages({ signal } = {}) {
  const data = await publicGet(`${API_BASE}/api/pricing/packages/`, { ttl: TTL.LIST, errorMessage: 'Failed to fetch pricing packages', signal });
  return unwrapList(data);
}

export async function getPricingPackageById(id, { signal } = {}) {
  return publicGet(`${API_BASE}/api/pricing/packages/${id}/`, { ttl: TTL.LIST, errorMessage: 'Package not found', signal });
}

export async function createPricingPackage(formData) {
  const headers = getAuthHeadersMultipart();
  const res = await fetch(`${API_BASE}/api/pricing/packages/`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || JSON.stringify(err) || 'Create failed');
  }
  cache.invalidate('/api/pricing/packages');
  return res.json();
}

export async function updatePricingPackage(id, formData) {
  const headers = getAuthHeadersMultipart();
  const res = await fetch(`${API_BASE}/api/pricing/packages/${id}/`, {
    method: 'PUT',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || JSON.stringify(err) || 'Update failed');
  }
  cache.invalidate('/api/pricing/packages');
  return res.json();
}

export async function deletePricingPackage(id) {
  const res = await fetch(`${API_BASE}/api/pricing/packages/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/pricing/packages');
}

// --- Scholarship opportunities (public: GET active; admin: full CRUD) ---
export async function getScholarshipOpportunities({ signal } = {}) {
  const data = await publicGet(`${API_BASE}/api/scholarships/opportunities/`, { ttl: TTL.LIST, errorMessage: 'Failed to fetch scholarship opportunities', signal });
  return unwrapList(data);
}

export async function getScholarshipOpportunitiesAdmin() {
  const data = await adminGet(`${API_BASE}/api/scholarships/opportunities/`, { errorMessage: 'Failed to fetch scholarship opportunities' });
  return unwrapList(data);
}

export async function createScholarshipOpportunity(data) {
  const res = await fetch(`${API_BASE}/api/scholarships/opportunities/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.university_name && err.university_name[0]) || JSON.stringify(err) || 'Create failed');
  }
  cache.invalidate('/api/scholarships/opportunities');
  return res.json();
}

export async function updateScholarshipOpportunity(id, data) {
  const res = await fetch(`${API_BASE}/api/scholarships/opportunities/${id}/`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || 'Update failed');
  }
  cache.invalidate('/api/scholarships/opportunities');
  return res.json();
}

export async function deleteScholarshipOpportunity(id) {
  const res = await fetch(`${API_BASE}/api/scholarships/opportunities/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/scholarships/opportunities');
}

// --- Home Partners (public: GET; admin: full CRUD) ---
export async function getPartners({ signal } = {}) {
  const data = await publicGet(`${API_BASE}/api/blog/partners/`, { ttl: TTL.CONTENT, errorMessage: 'Failed to fetch partners', signal });
  return unwrapList(data);
}

export async function createPartner(formData) {
  const headers = getAuthHeadersMultipart();
  const res = await fetch(`${API_BASE}/api/blog/partners/`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || 'Create failed');
  }
  cache.invalidate('/api/blog/partners');
  return res.json();
}

export async function updatePartner(id, formData) {
  const headers = getAuthHeadersMultipart();
  const res = await fetch(`${API_BASE}/api/blog/partners/${id}/`, {
    method: 'PUT',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || 'Update failed');
  }
  cache.invalidate('/api/blog/partners');
  return res.json();
}

export async function deletePartner(id) {
  const res = await fetch(`${API_BASE}/api/blog/partners/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/partners');
}

// --- About page statistics (public: GET; admin: full CRUD) ---
export async function getAboutStats({ signal } = {}) {
  const data = await publicGet(`${API_BASE}/api/blog/about-stats/`, { ttl: TTL.CONTENT, errorMessage: 'Failed to fetch about stats', signal });
  return unwrapList(data);
}

export async function createAboutStat(data) {
  const res = await fetch(`${API_BASE}/api/blog/about-stats/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || 'Create failed');
  }
  cache.invalidate('/api/blog/about-stats');
  return res.json();
}

export async function updateAboutStat(id, data) {
  const res = await fetch(`${API_BASE}/api/blog/about-stats/${id}/`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || 'Update failed');
  }
  cache.invalidate('/api/blog/about-stats');
  return res.json();
}

export async function deleteAboutStat(id) {
  const res = await fetch(`${API_BASE}/api/blog/about-stats/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/about-stats');
}

// --- Testimonials / Success Stories (public: GET; admin: full CRUD) ---
export async function getTestimonials({ signal } = {}) {
  const data = await publicGet(`${API_BASE}/api/blog/testimonials/`, { ttl: TTL.LIST, errorMessage: 'Failed to fetch testimonials', signal });
  return unwrapList(data);
}

export async function getTestimonialById(id, { signal } = {}) {
  return publicGet(`${API_BASE}/api/blog/testimonials/${id}/`, { ttl: TTL.LIST, errorMessage: 'Testimonial not found', signal });
}

export async function createTestimonial(formData) {
  const headers = getAuthHeadersMultipart();
  const res = await fetch(`${API_BASE}/api/blog/testimonials/`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || 'Create failed');
  }
  cache.invalidate('/api/blog/testimonials');
  return res.json();
}

export async function updateTestimonial(id, formData) {
  const headers = getAuthHeadersMultipart();
  const res = await fetch(`${API_BASE}/api/blog/testimonials/${id}/`, {
    method: 'PATCH',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || 'Update failed');
  }
  cache.invalidate('/api/blog/testimonials');
  return res.json();
}

export async function deleteTestimonial(id) {
  const res = await fetch(`${API_BASE}/api/blog/testimonials/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/testimonials');
}

// --- Home Counsellors (public: GET active only; admin: full CRUD incl. inactive) ---
export async function getCounsellors({ signal } = {}) {
  const data = await publicGet(`${API_BASE}/api/blog/counsellors/`, { ttl: TTL.LIST, errorMessage: 'Failed to fetch counsellors', signal });
  return unwrapList(data);
}

export async function getCounsellorsAdmin() {
  const data = await adminGet(`${API_BASE}/api/blog/counsellors/`, { errorMessage: 'Failed to fetch counsellors' });
  return unwrapList(data);
}

export async function createCounsellor(formData) {
  const res = await fetch(`${API_BASE}/api/blog/counsellors/`, {
    method: 'POST',
    headers: getAuthHeadersMultipart(),
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || 'Create failed');
  }
  cache.invalidate('/api/blog/counsellors');
  return res.json();
}

export async function updateCounsellor(id, formData) {
  const res = await fetch(`${API_BASE}/api/blog/counsellors/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeadersMultipart(),
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || 'Update failed');
  }
  cache.invalidate('/api/blog/counsellors');
  return res.json();
}

export async function deleteCounsellor(id) {
  const res = await fetch(`${API_BASE}/api/blog/counsellors/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/counsellors');
}

// --- Admission Results (public: GET active only; admin: full CRUD incl. inactive) ---
export async function getAdmissionResults({ signal } = {}) {
  const data = await publicGet(`${API_BASE}/api/blog/admission-results/`, { ttl: TTL.LIST, errorMessage: 'Failed to fetch admission results', signal });
  return unwrapList(data);
}

export async function getAdmissionResultsAdmin() {
  const data = await adminGet(`${API_BASE}/api/blog/admission-results/`, { errorMessage: 'Failed to fetch admission results' });
  return unwrapList(data);
}

export async function createAdmissionResult(payload) {
  const res = await fetch(`${API_BASE}/api/blog/admission-results/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || Object.values(err)[0]?.[0] || 'Create failed');
  }
  cache.invalidate('/api/blog/admission-results');
  return res.json();
}

export async function updateAdmissionResult(id, payload) {
  const res = await fetch(`${API_BASE}/api/blog/admission-results/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || Object.values(err)[0]?.[0] || 'Update failed');
  }
  cache.invalidate('/api/blog/admission-results');
  return res.json();
}

export async function deleteAdmissionResult(id) {
  const res = await fetch(`${API_BASE}/api/blog/admission-results/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/admission-results');
}

// --- Countries (public: GET active; admin: full CRUD with token) ---
export async function getCountries({ signal } = {}) {
  const data = await publicGet(`${API_BASE}/api/blog/countries/`, { ttl: TTL.CONTENT, errorMessage: 'Failed to fetch countries', signal });
  return unwrapList(data);
}

export async function getCountriesAdmin() {
  const data = await adminGet(`${API_BASE}/api/blog/countries/`, { errorMessage: 'Failed to fetch countries' });
  return unwrapList(data);
}

export async function getCountryBySlug(slug, { signal } = {}) {
  return publicGet(`${API_BASE}/api/blog/countries/${slug}/`, { ttl: TTL.CONTENT, errorMessage: 'Country not found', signal });
}

export async function getCountryBySlugAdmin(slug) {
  return adminGet(`${API_BASE}/api/blog/countries/${slug}/`, { errorMessage: 'Country not found' });
}

export async function createCountry(data) {
  const isFormData = data instanceof FormData;
  const res = await fetch(`${API_BASE}/api/blog/countries/`, {
    method: 'POST',
    headers: isFormData ? getAuthHeadersMultipart() : getAuthHeaders(),
    body: isFormData ? data : JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || JSON.stringify(err) || 'Create failed');
  }
  cache.invalidate('/api/blog/countries');
  return res.json();
}

export async function updateCountry(slug, data) {
  const isFormData = data instanceof FormData;
  const res = await fetch(`${API_BASE}/api/blog/countries/${slug}/`, {
    method: isFormData ? 'PATCH' : 'PUT',
    headers: isFormData ? getAuthHeadersMultipart() : getAuthHeaders(),
    body: isFormData ? data : JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || 'Update failed');
  }
  cache.invalidate('/api/blog/countries');
  return res.json();
}

export async function deleteCountry(slug) {
  const res = await fetch(`${API_BASE}/api/blog/countries/${slug}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/countries');
}

// --- Announcements (public: GET live only; admin: full CRUD with token) ---
// Time-sensitive: a short in-memory TTL and no localStorage persistence, so a
// returning visitor never sees a stale list while the fresh one loads.
const ANN_TTL = 30 * 1000;

export async function getFeaturedAnnouncements(limit = 3, { signal } = {}) {
  return publicGet(`${API_BASE}/api/blog/announcements/featured/?limit=${limit}`, {
    ttl: ANN_TTL, persist: false, errorMessage: 'Failed to fetch announcements', signal,
  });
}

/** Returns the single highest-priority pinned announcement, or null if none is pinned. */
export async function getAnnouncementBar({ signal } = {}) {
  const url = `${API_BASE}/api/blog/announcements/bar/`;
  return withAbort(cache.dedupe(url, async () => {
    const res = await fetch(url);
    if (res.status === 204) return null;
    if (!res.ok) throw new Error('Failed to fetch the announcement bar');
    return res.json();
  }, { ttl: ANN_TTL, persist: false }), signal);
}

export async function getAnnouncements(params = {}, { signal } = {}) {
  const q = new URLSearchParams(params).toString();
  const url = `${API_BASE}/api/blog/announcements/${q ? '?' + q : ''}`;
  return publicGet(url, { ttl: ANN_TTL, persist: false, errorMessage: 'Failed to fetch announcements', signal });
}

export async function getAnnouncementBySlug(slug, { signal } = {}) {
  return publicGet(`${API_BASE}/api/blog/announcements/${slug}/`, {
    ttl: ANN_TTL, persist: false, errorMessage: 'Failed to load this announcement. Please try again.',
    notFoundMessage: 'Announcement not found', signal,
  });
}

/** Admin: list all announcements (including drafts/scheduled) when authenticated. */
export async function getAnnouncementsAdmin(params = {}) {
  const q = new URLSearchParams(params).toString();
  const data = await adminGet(`${API_BASE}/api/blog/announcements/${q ? '?' + q : ''}`, {
    errorMessage: 'Failed to fetch announcements',
  });
  return unwrapList(data);
}

export async function getAnnouncementBySlugAdmin(slug) {
  return adminGet(`${API_BASE}/api/blog/announcements/${slug}/`, { errorMessage: 'Announcement not found' });
}

export async function createAnnouncement(formData) {
  const res = await fetch(`${API_BASE}/api/blog/announcements/`, {
    method: 'POST',
    headers: getAuthHeadersMultipart(),
    body: formData,
  });
  throwIfUnauthorized(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || 'Create failed');
  }
  cache.invalidate('/api/blog/announcements');
  return res.json();
}

export async function updateAnnouncement(slug, formData) {
  const res = await fetch(`${API_BASE}/api/blog/announcements/${slug}/`, {
    method: 'PATCH',
    headers: getAuthHeadersMultipart(),
    body: formData,
  });
  throwIfUnauthorized(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || 'Update failed');
  }
  cache.invalidate('/api/blog/announcements');
  return res.json();
}

export async function deleteAnnouncement(slug) {
  const res = await fetch(`${API_BASE}/api/blog/announcements/${slug}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/announcements');
}

// --- Calendar events (public: GET is_public only; admin: full CRUD) ---
export async function getCalendarEvents(params = {}, { signal } = {}) {
  const q = new URLSearchParams(params).toString();
  const url = `${API_BASE}/api/blog/calendar-events/${q ? '?' + q : ''}`;
  return publicGet(url, { ttl: TTL.LIST, errorMessage: 'Failed to fetch calendar events', signal });
}

export async function getCalendarEventsAdmin(params = {}) {
  const q = new URLSearchParams(params).toString();
  const data = await adminGet(`${API_BASE}/api/blog/calendar-events/${q ? '?' + q : ''}`, {
    errorMessage: 'Failed to fetch calendar events',
  });
  return unwrapList(data);
}

export async function createCalendarEvent(data) {
  const res = await fetch(`${API_BASE}/api/blog/calendar-events/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  throwIfUnauthorized(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || 'Create failed');
  }
  cache.invalidate('/api/blog/calendar-events');
  return res.json();
}

export async function updateCalendarEvent(id, data) {
  const res = await fetch(`${API_BASE}/api/blog/calendar-events/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  throwIfUnauthorized(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || JSON.stringify(err) || 'Update failed');
  }
  cache.invalidate('/api/blog/calendar-events');
  return res.json();
}

export async function deleteCalendarEvent(id) {
  const res = await fetch(`${API_BASE}/api/blog/calendar-events/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/blog/calendar-events');
}

// --- Announcement subscriptions ("notify me") — public: create only;
// admin: list only. No update/delete — see AnnouncementSubscriptionViewSet. ---
export async function createAnnouncementSubscription(data) {
  const res = await fetch(`${API_BASE}/api/blog/announcement-subscriptions/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    if (res.status === 429) throw new Error("You've hit the limit for this — please try again later.");
    const err = await res.json().catch(() => ({}));
    const msg = err.non_field_errors?.[0] || err.detail || Object.values(err)[0]?.[0] || 'Could not save your request.';
    throw new Error(msg);
  }
  return res.json();
}

export async function getAnnouncementSubscriptionsAdmin() {
  const data = await adminGet(`${API_BASE}/api/blog/announcement-subscriptions/`, {
    errorMessage: 'Failed to fetch subscriptions',
  });
  return unwrapList(data);
}

// Shared "public throttled JSON POST" error handling — same 429/field-error
// extraction as createAnnouncementSubscription above, reused by every
// public-create-only endpoint below instead of repeating it three times.
async function submitPublic(url, data) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    if (res.status === 429) throw new Error("You've hit the limit for this — please try again later.");
    const err = await res.json().catch(() => ({}));
    const msg = err.message || err.non_field_errors?.[0] || err.detail || Object.values(err.errors || err)?.[0]?.[0] || 'Could not submit your request.';
    throw new Error(msg);
  }
  return res.json();
}

// --- Job Search Support inquiries — public: create only (multipart, résumé
// upload); admin: list + toggle `contacted`. See JobSearchInquiryViewSet. ---
export async function createJobSearchInquiry(fields, resumeFile) {
  const form = new FormData();
  Object.entries(withAttribution(fields)).forEach(([k, v]) => { if (v !== '' && v != null) form.append(k, v); });
  if (resumeFile) form.append('resume_file', resumeFile);
  const res = await fetch(`${API_BASE}/api/blog/job-search-inquiries/`, { method: 'POST', body: form });
  if (!res.ok) {
    if (res.status === 429) throw new Error("You've hit the limit for this — please try again later.");
    const err = await res.json().catch(() => ({}));
    const msg = err.non_field_errors?.[0] || err.detail || Object.values(err)[0]?.[0] || 'Could not submit your inquiry.';
    throw new Error(msg);
  }
  return res.json();
}

export async function getJobSearchInquiriesAdmin() {
  const data = await adminGet(`${API_BASE}/api/blog/job-search-inquiries/`, { errorMessage: 'Failed to fetch job search inquiries' });
  return unwrapList(data);
}

export async function toggleJobSearchInquiryContacted(id, contacted) {
  const res = await fetch(`${API_BASE}/api/blog/job-search-inquiries/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ contacted }),
  });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

// --- Emergency Support requests — public: create only; admin: list +
// toggle `contacted`. See EmergencySupportRequestViewSet. ---
export async function createEmergencySupportRequest(data) {
  return submitPublic(`${API_BASE}/api/blog/emergency-support-requests/`, data);
}

export async function getEmergencySupportRequestsAdmin() {
  const data = await adminGet(`${API_BASE}/api/blog/emergency-support-requests/`, { errorMessage: 'Failed to fetch emergency support requests' });
  return unwrapList(data);
}

export async function toggleEmergencySupportContacted(id, contacted) {
  const res = await fetch(`${API_BASE}/api/blog/emergency-support-requests/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ contacted }),
  });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

// --- Education Consultation requests — public: create only; admin: list +
// toggle `contacted`. See EducationConsultationRequestViewSet. ---
export async function createEducationConsultationRequest(data) {
  return submitPublic(`${API_BASE}/api/blog/education-consultation-requests/`, withAttribution(data));
}

export async function getEducationConsultationRequestsAdmin() {
  const data = await adminGet(`${API_BASE}/api/blog/education-consultation-requests/`, { errorMessage: 'Failed to fetch education consultation requests' });
  return unwrapList(data);
}

export async function toggleEducationConsultationContacted(id, contacted) {
  const res = await fetch(`${API_BASE}/api/blog/education-consultation-requests/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ contacted }),
  });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

// --- Tourism Consultation requests — public: create only; admin: list +
// toggle `contacted`. See TourismConsultationRequestViewSet. ---
export async function createTourismConsultationRequest(data) {
  return submitPublic(`${API_BASE}/api/blog/tourism-consultation-requests/`, withAttribution(data));
}

export async function getTourismConsultationRequestsAdmin() {
  const data = await adminGet(`${API_BASE}/api/blog/tourism-consultation-requests/`, { errorMessage: 'Failed to fetch tourism consultation requests' });
  return unwrapList(data);
}

export async function toggleTourismConsultationContacted(id, contacted) {
  const res = await fetch(`${API_BASE}/api/blog/tourism-consultation-requests/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ contacted }),
  });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

// Staff: re-push an enquiry that did not reach the management system.
// kind: 'tourism' | 'education' | 'job'. See _ConsultationSyncMixin.resync.
const CONSULTATION_RESYNC_PATHS = {
  tourism: 'tourism-consultation-requests',
  education: 'education-consultation-requests',
  job: 'job-search-inquiries',
};

export async function resyncConsultationAdmin(kind, id) {
  const path = CONSULTATION_RESYNC_PATHS[kind];
  if (!path) throw new Error('Unknown enquiry type');
  const res = await fetch(`${API_BASE}/api/blog/${path}/${id}/resync/`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Retry failed');
  return res.json();
}

// --- Alumni Network Access signups — public: create only (syncs to VEC_MS,
// creates an Alumni record pending staff approval there); admin: list only
// (read-only — approval happens in VEC_MS's own Alumni admin screen, not
// here). See AlumniSignupRequestViewSet. ---
export async function createAlumniSignup(data) {
  return submitPublic(`${API_BASE}/api/blog/alumni-signups/`, data);
}

export async function getAlumniSignupsAdmin() {
  const data = await adminGet(`${API_BASE}/api/blog/alumni-signups/`, { errorMessage: 'Failed to fetch alumni signups' });
  return unwrapList(data);
}

// --- Medical Tourism registrations — public: create only (multipart:
// passport/medical-record/referral-letter/scan uploads). Posts to the
// separate medical-tourism Django app, not the blog app the requests above
// use — same multipart-FormData + success/errors contract as the student
// registration flow (SignUpWizard's /api/registrations/register/), so
// errors are combined the same way: a single message built from `errors`
// when present, else `message`, else the HTTP status. ---
export async function registerMedicalTourismClient(fields, files) {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => { if (v !== '' && v != null) form.append(k, v); });
  Object.entries(files || {}).forEach(([k, file]) => { if (file) form.append(k, file); });

  const res = await fetch(`${API_BASE}/api/medical-tourism/register/`, {
    method: 'POST',
    body: form,
  });

  let data = {};
  try { data = await res.json(); } catch (_) {}

  if (res.status === 429) throw new Error("You've hit the limit for this — please try again later.");

  if (!res.ok || data.success === false) {
    const errDetail = data.errors
      ? Object.entries(data.errors).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ')
      : (data.message || `HTTP ${res.status}`);
    throw new Error(errDetail);
  }

  return data;
}

// --- Universities (public) ---
export async function getUniversities(params = {}, { signal } = {}) {
  const q = new URLSearchParams(params).toString();
  const url = `${API_BASE}/api/universities/${q ? '?' + q : ''}`;
  return publicGet(url, { ttl: TTL.LIST, errorMessage: 'Failed to fetch universities', signal });
}

export async function getUniversityBySlug(slug, { signal } = {}) {
  return publicGet(`${API_BASE}/api/universities/${slug}/`, {
    ttl: TTL.LIST,
    errorMessage: 'Failed to load this university. Please try again.',
    notFoundMessage: 'University not found',
    signal,
  });
}

export async function searchUniversities(params = {}, { signal } = {}) {
  const q = new URLSearchParams(params).toString();
  const url = `${API_BASE}/api/universities/search/${q ? '?' + q : ''}`;
  return publicGet(url, { ttl: TTL.LIST, errorMessage: 'Failed to search universities', signal });
}

export async function searchPrograms(params = {}, { signal } = {}) {
  const q = new URLSearchParams(params).toString();
  const url = `${API_BASE}/api/universities/programs/search/${q ? '?' + q : ''}`;
  return publicGet(url, { ttl: TTL.LIST, errorMessage: 'Failed to search programs', signal });
}

// --- Admin: Universities (CRUD, auth required) ---
const UNIVERSITIES_ADMIN = `${API_BASE}/api/universities/admin`;

export async function getUniversitiesAdmin(params = {}) {
  const q = new URLSearchParams(params).toString();
  const url = `${UNIVERSITIES_ADMIN}/universities/${q ? '?' + q : ''}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Failed to fetch universities');
  const data = await res.json();
  // Paginated envelope ({count, next, previous, results}) is returned as-is
  // so callers can show a total count / page through results; callers that
  // just want the array can still do `Array.isArray(data) ? data : data.results`.
  return data;
}

export async function getUniversityById(id) {
  const res = await fetch(`${UNIVERSITIES_ADMIN}/universities/${id}/`, { headers: getAuthHeaders() });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('University not found');
  return res.json();
}

export async function createUniversity(data) {
  const isFormData = data instanceof FormData;
  const res = await fetch(`${UNIVERSITIES_ADMIN}/universities/`, {
    method: 'POST',
    headers: isFormData ? getAuthHeadersMultipart() : getAuthHeaders(),
    body: isFormData ? data : JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || JSON.stringify(err) || 'Create failed');
  }
  cache.invalidate('/api/universities');
  return res.json();
}

export async function updateUniversity(id, data) {
  const isFormData = data instanceof FormData;
  const res = await fetch(`${UNIVERSITIES_ADMIN}/universities/${id}/`, {
    method: isFormData ? 'PATCH' : 'PUT',
    headers: isFormData ? getAuthHeadersMultipart() : getAuthHeaders(),
    body: isFormData ? data : JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || JSON.stringify(err) || 'Update failed');
  }
  cache.invalidate('/api/universities');
  return res.json();
}

export async function deleteUniversity(id) {
  const res = await fetch(`${UNIVERSITIES_ADMIN}/universities/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/universities');
}

// --- Admin: Programs (CRUD, auth required) ---
export async function getProgramsAdmin(params = {}) {
  const q = new URLSearchParams(params).toString();
  const url = `${UNIVERSITIES_ADMIN}/programs/${q ? '?' + q : ''}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Failed to fetch programs');
  const data = await res.json();
  // Paginated envelope ({count, next, previous, results}) is returned as-is
  // so callers can show a total count / page through results.
  return data;
}

export async function getProgramById(id) {
  const res = await fetch(`${UNIVERSITIES_ADMIN}/programs/${id}/`, { headers: getAuthHeaders() });
  throwIfUnauthorized(res);
  if (!res.ok) throw new Error('Program not found');
  return res.json();
}

export async function createProgram(data) {
  const res = await fetch(`${UNIVERSITIES_ADMIN}/programs/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || (err.name && err.name[0]) || JSON.stringify(err) || 'Create failed');
  }
  cache.invalidate('/api/universities');
  return res.json();
}

export async function updateProgram(id, data) {
  const res = await fetch(`${UNIVERSITIES_ADMIN}/programs/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = err.detail
      || (err.name && err.name[0])
      || (err.university && `University: ${err.university}`)
      || JSON.stringify(err)
      || 'Update failed';
    throw new Error(msg);
  }
  cache.invalidate('/api/universities');
  return res.json();
}

export async function deleteProgram(id) {
  const res = await fetch(`${UNIVERSITIES_ADMIN}/programs/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
  cache.invalidate('/api/universities');
}
